if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.observer = null;
        this.sortedQueue = [];
        this.stockReady = false;
      }

      connectedCallback() {
        this.nextUrl = this.dataset.nextUrl;
        this.sectionId = this.dataset.sectionId;
        this.gridId = this.dataset.gridId || 'product-grid';
        this.spinner = this.querySelector('[data-infinite-scroll-spinner]');
        this.grid = document.getElementById(this.gridId);
        this.collection = this.grid?.closest('.collection');
        this.pageSize = Number(this.dataset.productsPerPage || this.grid?.dataset?.productsPerPage || 16);
        this.stockOrder = this.grid?.dataset?.productsOrder;
        this.useStockSort = this.stockOrder === 'stock_high' || this.stockOrder === 'stock_low';

        if (!this.sectionId) return;

        if (this.useStockSort && this.nextUrl) {
          this.prepareStockOrder();
          return;
        }

        if (!this.nextUrl) return;

        this.startObserver();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      startObserver() {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.loadMore();
            }
          },
          { rootMargin: '400px 0px' }
        );
        this.observer.observe(this);
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.spinner?.classList.toggle('hidden', !isLoading);

        if (isLoading) {
          this.setAttribute('aria-busy', 'true');
          return;
        }

        this.removeAttribute('aria-busy');
      }

      finishStockLoading() {
        this.collection?.classList.remove('stock-sort-loading', 'loading');
        this.setLoading(false);
      }

      sortItems(productItems) {
        productItems.sort((first, second) => {
          const firstStock = Number(first.dataset.inventory || 0);
          const secondStock = Number(second.dataset.inventory || 0);
          return this.stockOrder === 'stock_low' ? firstStock - secondStock : secondStock - firstStock;
        });
      }

      uniqueProductItems(items) {
        const seen = new Set();
        return items.filter((item) => {
          const id = item.dataset.productId;
          if (!id) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      }

      renderFirstPage(productItems, promoItems) {
        const firstPage = productItems.slice(0, this.pageSize);
        this.sortedQueue = productItems.slice(this.pageSize);

        const fragment = document.createDocumentFragment();
        const promoAfter = 2;
        firstPage.forEach((item, index) => {
          if (index === promoAfter) {
            promoItems.forEach((promo) => fragment.appendChild(promo));
          }
          fragment.appendChild(item);
        });
        if (firstPage.length <= promoAfter) {
          promoItems.forEach((promo) => fragment.appendChild(promo));
        }

        this.grid.replaceChildren(fragment);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
      }

      buildPageUrl(page) {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', this.sectionId);
        return url.toString();
      }

      parseProductItems(htmlText) {
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const grid = doc.getElementById(this.gridId);
        if (!grid) return [];

        return Array.from(grid.querySelectorAll('.grid__item:not(.promocion)')).map((item) =>
          document.importNode(item, true)
        );
      }

      async fetchRemainingPages() {
        const pages = Number(this.dataset.totalPages || 0);
        const current = Number(this.dataset.currentPage || 1);
        const items = [];

        if (pages > current) {
          const pageNumbers = [];
          for (let page = current + 1; page <= pages; page += 1) {
            pageNumbers.push(page);
          }

          const batchSize = 5;
          for (let i = 0; i < pageNumbers.length; i += batchSize) {
            const batch = pageNumbers.slice(i, i + batchSize);
            const htmlPages = await Promise.all(
              batch.map(async (page) => {
                const response = await fetch(this.buildPageUrl(page));
                if (!response.ok) throw new Error(`Failed to load page: ${response.status}`);
                return response.text();
              })
            );
            htmlPages.forEach((text) => {
              items.push(...this.parseProductItems(text));
            });
          }

          return items;
        }

        let nextUrl = this.nextUrl;
        while (nextUrl) {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);
          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`Failed to load page: ${response.status}`);
          const text = await response.text();
          items.push(...this.parseProductItems(text));
          const html = new DOMParser().parseFromString(text, 'text/html');
          nextUrl = html.querySelector('infinite-scroll')?.dataset?.nextUrl || '';
        }

        return items;
      }

      async prepareStockOrder() {
        if (!this.grid) return;

        this.setLoading(true);
        this.collection?.classList.add('stock-sort-loading', 'loading');

        try {
          const promoItems = Array.from(this.grid.querySelectorAll('.grid__item.promocion'));
          const currentProducts = Array.from(this.grid.querySelectorAll('.grid__item:not(.promocion)'));
          const remainingProducts = await this.fetchRemainingPages();
          const productItems = this.uniqueProductItems(currentProducts.concat(remainingProducts));

          this.sortItems(productItems);
          this.renderFirstPage(productItems, promoItems);
          this.nextUrl = '';
          this.dataset.nextUrl = '';
          this.stockReady = true;
          this.finishStockLoading();

          if (this.sortedQueue.length) {
            this.startObserver();
          } else {
            this.remove();
          }
        } catch (error) {
          console.error(error);
          this.finishStockLoading();
          if (this.nextUrl) this.startObserver();
        }
      }

      appendFromQueue() {
        if (!this.grid || !this.sortedQueue.length) {
          this.observer?.disconnect();
          this.remove();
          return;
        }

        this.setLoading(true);
        const nextItems = this.sortedQueue.splice(0, this.pageSize);
        nextItems.forEach((item) => this.grid.appendChild(item));

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }

        this.setLoading(false);

        if (!this.sortedQueue.length) {
          this.observer?.disconnect();
          this.remove();
        }
      }

      async loadMore() {
        if (this.useStockSort && this.stockReady) {
          if (this.loading) return;
          this.appendFromQueue();
          return;
        }

        if (this.loading || !this.nextUrl) return;

        const grid = this.grid || document.getElementById(this.gridId);
        if (!grid) return;

        this.setLoading(true);

        try {
          const url = new URL(this.nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`Failed to load page: ${response.status}`);

          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newGrid = html.getElementById(this.gridId);
          const newInfiniteScroll = html.querySelector('infinite-scroll');

          if (newGrid) {
            Array.from(newGrid.children).forEach((item) => {
              if (item.classList?.contains('promocion')) return;
              grid.appendChild(document.importNode(item, true));
            });

            if (typeof initializeScrollAnimationTrigger === 'function') {
              initializeScrollAnimationTrigger(grid);
            }
          }

          const nextUrl = newInfiniteScroll?.dataset?.nextUrl;
          if (nextUrl) {
            this.nextUrl = nextUrl;
            this.dataset.nextUrl = nextUrl;
          } else {
            this.nextUrl = '';
            this.dataset.nextUrl = '';
            this.observer?.disconnect();
            this.remove();
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
